import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';

import useProfileStyles from './profileStyles';
import { PlanFeatures } from '../Onboarding/OnboardingShell';
import { getSubscriptionData, SUBSCRIPTION_PLANS, TRIAL_DAYS } from '../../shared/Capabilities';
import { ONBOARDING_STEPS } from '../../shared/OnboardingOptions';
import { SUBSCRIPTIONS_LINK } from '../../common/urlLinks';
import { cx } from '../Home/HomeDashboard/dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';
import GuestSignInOptions from '../Onboarding/GuestSignInOptions';

const ONE_COL = '@media (max-width: 860px)';

const useStyles = makeStyles(() => ({
    layout: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        gap: '22px',
        alignItems: 'start',
        [ONE_COL]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    facts: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '18px',
        marginBottom: '22px',
        [ONE_COL]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    value: {
        margin: 0,
        fontSize: '1.5rem',
        lineHeight: 1.15,
        letterSpacing: '-0.03em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    trial: {
        marginTop: '22px',
        padding: '18px 20px',
        border: `1px solid ${REDESIGN_COLORS.amberBorder}`,
        borderRadius: '18px',
        background: REDESIGN_COLORS.amberSurface,
    },
    trialCta: {
        whiteSpace: 'normal',
        textAlign: 'center',
    },
    trialText: {
        margin: '0 0 12px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.92rem',
    },
}));

const expiresOn = (intl, subscription, planName) => {
    if (planName === SUBSCRIPTION_PLANS.FREE || !subscription.endDate) {
        return intl.formatMessage({ id: 'subscriptions.subscription.expiresNever' });
    }
    const end = new Date(subscription.endDate);
    if (end < new Date()) {
        return intl.formatMessage({ id: 'subscriptions.subscription.expiresExpired' });
    }
    return intl.formatDate(end, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

/**
 * The account's plan: name, expiry, the actions that apply (cancel a paid
 * plan, see the plans, start the trial) and what the plan includes.
 */
const CurrentSubscription = ({ profile, subscriptions }) => {
    const classes = useProfileStyles();
    const own = useStyles();
    const intl = useIntl();

    if (!profile || !subscriptions || !profile.subscription) {
        return null;
    }
    const data = getSubscriptionData(subscriptions, profile.subscription);
    const planName = data.subscriptionName;

    const paid = planName === SUBSCRIPTION_PLANS.BASIC || planName === SUBSCRIPTION_PLANS.PREMIUM;
    let cancelable = paid && !profile.subscription.canceledAt;
    let upgradable =
        (!cancelable && planName !== SUBSCRIPTION_PLANS.SPECIAL && planName !== SUBSCRIPTION_PLANS.PREMIUM) ||
        (profile.subscription.canceledAt && planName !== SUBSCRIPTION_PLANS.SPECIAL);
    if (profile.isGuest) {
        cancelable = false;
        upgradable = false;
    }
    const trialAvailable =
        !profile.isGuest && planName === SUBSCRIPTION_PLANS.FREE && profile?.subscription?.trialStarted === false;

    return (
        <div className={own.layout}>
            <div className={classes.card}>
                <div className={own.facts}>
                    <div>
                        <span className={classes.kicker}>
                            <FormattedMessage id="subscriptions.subscription.currentSubscription" />
                        </span>
                        <p className={own.value}>{planName}</p>
                    </div>
                    <div>
                        <span className={classes.kicker}>
                            <FormattedMessage id="subscriptions.subscription.expires" />
                        </span>
                        <p className={own.value}>{expiresOn(intl, profile.subscription, planName)}</p>
                    </div>
                </div>

                {profile.isGuest ? (
                    <GuestSignInOptions returnTo={SUBSCRIPTIONS_LINK} />
                ) : (
                    <div className={classes.actions}>
                        {upgradable ? (
                            <Link to={SUBSCRIPTIONS_LINK} className={classes.primary}>
                                <FormattedMessage id="redesign.cta.plans" />
                                <span aria-hidden="true">→</span>
                            </Link>
                        ) : null}
                        {cancelable ? (
                            <Link to={`/onboarding?step=${ONBOARDING_STEPS.CANCEL}`} className={classes.danger}>
                                <FormattedMessage id="subscriptions.subscription.cancel" />
                            </Link>
                        ) : null}
                    </div>
                )}

                {trialAvailable ? (
                    <div className={own.trial}>
                        <p className={own.trialText}>
                            <FormattedMessage id="onboarding.start.startTrialDescription" />
                        </p>
                        <Link
                            to={`/onboarding?step=${ONBOARDING_STEPS.START}&subscription=${SUBSCRIPTION_PLANS.TRIAL}&billAnnual=true&billEur=false`}
                            className={cx(classes.secondary, own.trialCta)}
                        >
                            <FormattedMessage id="subscriptions.subscription.startTrial" values={{ days: TRIAL_DAYS }} />
                            <span aria-hidden="true">→</span>
                        </Link>
                    </div>
                ) : null}
            </div>

            <div className={classes.card}>
                <h2 className={classes.cardTitle}>
                    <FormattedMessage id="redesign.onboarding.paid.includes" />
                </h2>
                <PlanFeatures data={data} />
            </div>
        </div>
    );
};

const mapStateToProps = (state) => {
    const { profile, mainAppState } = state;
    return { profile, subscriptions: mainAppState.subscriptions };
};

export default connect(mapStateToProps)(CurrentSubscription);
